"use strict";
/*******************************************************************************

    Contains class for managing UTXO in the wallet

    Copyright:
        Copyright (c) 2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WalletUTXOProvider = void 0;
var Amount_1 = require("../common/Amount");
var Hash_1 = require("../common/Hash");
var Types_1 = require("../net/response/Types");
var UnspentTxOutput_1 = require("../net/response/UnspentTxOutput");
var Utils_1 = require("../utils/Utils");
var Types_2 = require("./Types");
var jsbi_1 = __importDefault(require("jsbi"));
/**
 * Class for managing UTXO
 * Manage what is used and not used in payment for UTXO
 */
var WalletUTXOProvider = /** @class */ (function () {
    /**
     * Constructor
     * @param address The instance of the PublicKey
     * @param client  The instance of the BOAClient
     * @param balance_type The balance type
     */
    function WalletUTXOProvider(address, client, balance_type) {
        if (balance_type === void 0) { balance_type = Types_1.BalanceType.spendable; }
        this.address = address;
        this.items = [];
        this.client = client;
        this.balance_type = balance_type;
    }
    /**
     * Add UTXOs. If it already exists, ignore it.
     * After add, Two fields are used for sorting.
     * One is unlock_height and the other is amount.
     * The array is sorted in ascending order of unlock_height,
     * and in ascending order of amount when the same unlock_height is.
     * This allows a UTXO that is created first to be used first.
     * It also allows small amounts to be used first.
     * @param data The array of `UnspentTxOutput`
     */
    WalletUTXOProvider.prototype.add = function (data) {
        var _a;
        var _this = this;
        var old_length = this.items.length;
        (_a = this.items).push.apply(_a, data
            .filter(function (n) { return _this.items.find(function (m) { return m.utxo.data.compare(n.utxo.data) === 0; }) === undefined; })
            .map(function (n) { return new InternalUTXO(n.utxo, n.type, n.unlock_height, n.amount, n.height); }));
        if (this.items.length > old_length)
            this.items.sort(function (a, b) {
                var cmp = function (x, y) { return (jsbi_1.default.greaterThan(x, y) ? 1 : jsbi_1.default.lessThan(x, y) ? -1 : 0); };
                if (jsbi_1.default.notEqual(a.height, b.height))
                    return cmp(a.height, b.height);
                return Utils_1.Utils.compareBuffer(a.utxo.data, b.utxo.data);
            });
    };
    /**
     * Returns an array of UTXOs to be used for input of transactions
     * based on the amount entered.
     * @param amount The required amount
     * @param estimated_input_fee The estimated fee of the size of one TxInput
     * @returns Returns the available array of UTXO. If the available amount
     * is less than the requested amount, the empty array is returned.
     */
    WalletUTXOProvider.prototype.getUTXO = function (amount, estimated_input_fee) {
        if (estimated_input_fee === void 0) { estimated_input_fee = Amount_1.Amount.make(0); }
        return __awaiter(this, void 0, void 0, function () {
            var target_amount, result, _loop_1, this_1, state_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        result = [];
                        target_amount = Amount_1.Amount.make(amount);
                        _loop_1 = function () {
                            var sum, utxo, res_sum, estimated_amount, res;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        target_amount = Amount_1.Amount.add(target_amount, estimated_input_fee);
                                        sum = Amount_1.Amount.make(0);
                                        utxo = this_1.items
                                            .filter(function (n) { return !n.used; })
                                            .filter(function (n) {
                                            if (Amount_1.Amount.greaterThanOrEqual(sum, target_amount))
                                                return false;
                                            sum = Amount_1.Amount.add(sum, n.amount);
                                            n.used = true;
                                            target_amount = Amount_1.Amount.add(target_amount, estimated_input_fee);
                                            return true;
                                        })
                                            .map(function (n) { return new UnspentTxOutput_1.UnspentTxOutput(n.utxo, n.type, n.unlock_height, n.amount, n.height); });
                                        result.push.apply(result, utxo);
                                        res_sum = result.reduce(function (prev, value) { return Amount_1.Amount.add(prev, value.amount); }, Amount_1.Amount.make(0));
                                        estimated_amount = Amount_1.Amount.add(amount, Amount_1.Amount.multiply(estimated_input_fee, result.length));
                                        if (Amount_1.Amount.greaterThanOrEqual(res_sum, estimated_amount))
                                            return [2 /*return*/, "break"];
                                        target_amount = Amount_1.Amount.subtract(estimated_amount, res_sum);
                                        return [4 /*yield*/, this_1.client.getUTXOs(this_1.address, target_amount, this_1.balance_type, this_1.getLastUTXO())];
                                    case 1:
                                        res = _a.sent();
                                        if (res.code !== Types_2.WalletResultCode.Success || res.data === undefined) {
                                            return [2 /*return*/, { value: res }];
                                        }
                                        if (res.data.length === 0) {
                                            // Not Enough Amount
                                            this_1.items.forEach(function (m) {
                                                if (result.find(function (n) { return Hash_1.Hash.equal(m.utxo, n.utxo); }) !== undefined) {
                                                    m.used = false;
                                                }
                                            });
                                            result.length = 0;
                                            return [2 /*return*/, "break"];
                                        }
                                        this_1.add(res.data);
                                        return [2 /*return*/];
                                }
                            });
                        };
                        this_1 = this;
                        _a.label = 1;
                    case 1:
                        if (!true) return [3 /*break*/, 3];
                        return [5 /*yield**/, _loop_1()];
                    case 2:
                        state_1 = _a.sent();
                        if (typeof state_1 === "object")
                            return [2 /*return*/, state_1.value];
                        if (state_1 === "break")
                            return [3 /*break*/, 3];
                        return [3 /*break*/, 1];
                    case 3: return [2 /*return*/, {
                            code: Types_2.WalletResultCode.Success,
                            message: Types_2.WalletMessage.Success,
                            data: result,
                        }];
                }
            });
        });
    };
    WalletUTXOProvider.prototype.getLastUTXO = function () {
        return this.items.length === 0 ? undefined : this.items[this.items.length - 1].utxo;
    };
    WalletUTXOProvider.prototype.clear = function () {
        this.items.length = 0;
    };
    Object.defineProperty(WalletUTXOProvider.prototype, "length", {
        get: function () {
            return this.items.length;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * It is used to give back the previously taken UTXO without using it.
     * @param unused The array of UnspentTxOutput, previously taken UTXOs
     */
    WalletUTXOProvider.prototype.giveBack = function (unused) {
        var _loop_2 = function (elem) {
            var found = this_2.items.find(function (m) { return m.utxo.data.compare(elem.utxo.data) === 0; });
            if (found !== undefined)
                found.used = false;
        };
        var this_2 = this;
        for (var _i = 0, unused_1 = unused; _i < unused_1.length; _i++) {
            var elem = unused_1[_i];
            _loop_2(elem);
        }
    };
    return WalletUTXOProvider;
}());
exports.WalletUTXOProvider = WalletUTXOProvider;
/**
 * Define the internal UTXO
 */
var InternalUTXO = /** @class */ (function (_super) {
    __extends(InternalUTXO, _super);
    /**
     * Constructor
     * @param utxo          The hash of the UTXO
     * @param type          The type of the transaction
     * @param unlock_height The height of the block that can be used
     * @param amount        The monetary value of UTXO
     * @param height        The block height
     */
    function InternalUTXO(utxo, type, unlock_height, amount, height) {
        var _this = _super.call(this, utxo, type, unlock_height, amount, height) || this;
        if (jsbi_1.default.lessThanOrEqual(_this.unlock_height, jsbi_1.default.BigInt(0)))
            throw new Error("Positive unlock_height expected, not " + unlock_height.toString());
        if (Amount_1.Amount.lessThanOrEqual(_this.amount, Amount_1.Amount.ZERO_BOA))
            throw new Error("Positive amount expected, not " + amount.toString());
        _this.used = false;
        return _this;
    }
    return InternalUTXO;
}(UnspentTxOutput_1.UnspentTxOutput));
//# sourceMappingURL=WalletUTXOProvider.js.map